/**
 * @hausmeter/i18n — Texte und Anzeigeformate.
 *
 * Deutsch ist die führende Sprache. Übersetzungen liegen als TypeScript vor,
 * damit ein fehlender Schlüssel beim Bauen auffällt statt im Betrieb.
 */
import { de, type TranslationKey } from "./de.js";
import { en } from "./en.js";
import { DEFAULT_LOCALE, type Locale } from "./format.js";

export * from "./format.js";
export { de, en };
export type { TranslationKey };

const CATALOGS: Readonly<Record<Locale, Readonly<Record<TranslationKey, string>>>> = {
  "de-DE": de,
  "en-GB": en,
};

/**
 * Übersetzt einen Schlüssel. Der Rückgabetyp ist ein String und kein
 * `string | undefined`: unbekannte Schlüssel gibt es dank `TranslationKey` nicht.
 */
export function t(key: TranslationKey, locale: Locale = DEFAULT_LOCALE): string {
  return CATALOGS[locale][key];
}

/**
 * Ist diese Zeichenkette ein bekannter Schlüssel?
 *
 * Für Schlüssel, die **über die Systemgrenze** kommen — etwa `labelKey` eines
 * Integrationstyps (OQ-026): Dort ist der Wert ein `string`, und die Typprüfung
 * kann ihn nicht mehr halten. Ein unbekannter Schlüssel ist ein neuer Typ ohne
 * Übersetzung; wer ihn anzeigt statt zu raten, sieht sofort, welcher fehlt.
 */
export function isTranslationKey(value: string): value is TranslationKey {
  return Object.hasOwn(de, value);
}

/** Erzeugt eine gebundene Übersetzungsfunktion für eine Sprache. */
export function translator(locale: Locale): (key: TranslationKey) => string {
  const catalog = CATALOGS[locale];
  return (key) => catalog[key];
}

/**
 * Wählt die passende Sprache aus einer Browser- oder Geräteangabe.
 * Fällt auf Deutsch zurück — das ist der Markt, für den HausMeter gebaut wird.
 */
export function resolveLocale(preferred: readonly string[]): Locale {
  for (const candidate of preferred) {
    if (candidate.startsWith("de")) return "de-DE";
    if (candidate.startsWith("en")) return "en-GB";
  }
  return DEFAULT_LOCALE;
}
